import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  NotificationFormCompletedValue,
  NotificationResponseTypes,
  NotificationTypeCodes,
} from '../constants';
import { gqlGetNotificationResponseInfo, gqlRespondToNotification } from '../graphql';
import { CollectionpointFormValues, CollectionpointUpdateForm } from './cp-update-form';

function questionForType(typeCode?: string, responseType?: string): string {
  if (responseType === NotificationResponseTypes.Form) return '';
  if (typeCode === NotificationTypeCodes.BinReceived) return 'Παραλάβατε τον κάδο;';
  if (typeCode === NotificationTypeCodes.HasBinYearly) return 'Έχετε κάδο;';
  return 'Ποια είναι η απάντησή σας;';
}

function answerLabel(value?: string | null): string {
  if (value === 'yes') return 'Ναι';
  if (value === 'no') return 'Όχι';
  if (value === NotificationFormCompletedValue || value === 'completed') {
    return 'Η φόρμα συμπληρώθηκε';
  }
  return value ?? '';
}

function apolloMessage(error: any): string {
  return (
    error?.graphQLErrors?.[0]?.message ||
    error?.networkError?.message ||
    error?.message ||
    'Παρουσιάστηκε σφάλμα'
  );
}

function toFormPayload(values: CollectionpointFormValues) {
  return {
    name: values.name,
    addressLine1: values.addressLine1,
    addressLine2: values.addressLine2 || null,
    postalCode: values.postalCode || null,
    addressArea: values.addressArea || null,
    addressCity: values.addressCity || null,
    regionId: values.regionId ? Number(values.regionId) : null,
    subregionId: values.subregionId ? Number(values.subregionId) : null,
    contactPerson: values.contactPerson || null,
    phone: values.phone,
    mobile: values.mobile || null,
    email: values.email || null,
    companyVat: values.companyVat || null,
    companyTaxOffice: values.companyTaxOffice || null,
  };
}

/** Δημόσια σελίδα απάντησης από το mail — χωρίς login και χωρίς μενού
 * Από το token της ενημέρωσης παίρνουμε τα στοιχεία της ενημέρωσης και το τύπο της απάντησης.
 * Αν είναι τύπου Form, εμφανίζουμε το φορμάκι για την απάντηση.
 * Αν είναι τύπου YesNo, εμφανίζουμε τα κουμπιά Ναι και Όχι.
 * Αν είναι τύπου BinReceived ή HasBinYearly, εμφανίζουμε το κουμπί Ναι ή Όχι.
 * Αν είναι τύπου BinReceived, εμφανίζουμε το κουμπί Ναι ή Όχι.
 * Αν είναι τύπου HasBinYearly, εμφανίζουμε το κουμπί Ναι ή Όχι.
 * Αν είναι τύπου HasBinYearly, εμφανίζουμε το κουμπί Ναι ή Όχι.
 */
export const NotificationRespondPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { token } = useParams<{ token: string }>();
  const autoTried = useRef(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAnswer, setSavedAnswer] = useState<string | null>(null);

  /** Public query που πακετάρει τα στοιχεία που χρειάζεται η φόρμα */
  const { data, loading, error, refetch } = useQuery(gqlGetNotificationResponseInfo, {
    variables: { token },
    skip: !token,
    fetchPolicy: 'network-only',
  });
  const [respond, { loading: submitting }] = useMutation(gqlRespondToNotification);

  const info = data?.getNotificationResponseInfo;
  const queryAnswer = new URLSearchParams(location.search).get('a');
  const currentAnswer = savedAnswer ?? info?.responseValue ?? null;
  const isForm = info?.responseType === NotificationResponseTypes.Form;
  const canAnswer =
    !!info &&
    !info.expired &&
    !info.closed &&
    info.responseType === NotificationResponseTypes.YesNo;
  const canEditForm = !!info && isForm && !info.expired && !info.closed;

  const submitAnswer = async (answer: string) => {
    if (!token || submitting) return;
    setSubmitError(null);
    try {
      const result = await respond({ variables: { token, answer } });
      const payload = result.data?.respondToNotification;
      if (!payload?.ok) {
        throw new Error(payload?.message || 'Αποτυχία καταχώρησης');
      }
      setSavedAnswer(payload.responseValue);
      // Αφαιρούμε το ?a= ώστε το refresh να μην ξαναστείλει την ίδια απάντηση
      if (location.search) {
        history.replace({ pathname: location.pathname, search: '' });
      }
      await refetch();
    } catch (err) {
      setSubmitError(apolloMessage(err));
    }
  };

  const submitForm = async (values: CollectionpointFormValues) => {
    if (!token || submitting) return;
    setSubmitError(null);
    try {
      const result = await respond({
        variables: { token, answer: NotificationFormCompletedValue, form: toFormPayload(values) },
      });
      const payload = result.data?.respondToNotification;
      if (!payload?.ok) {
        throw new Error(payload?.message || 'Αποτυχία καταχώρησης');
      }
      setSavedAnswer(payload.responseValue);
      await refetch();
    } catch (err) {
      setSubmitError(apolloMessage(err));
    }
  };

  useEffect(() => {
    if (!canAnswer || autoTried.current || submitting) return;
    const auto = String(queryAnswer ?? '').toLowerCase();
    if (auto !== 'yes' && auto !== 'no') return;
    autoTried.current = true;
    submitAnswer(auto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAnswer, queryAnswer]);

  return (
    <div
      className={`d-flex min-vh-100 justify-content-center p-3 ${
        isForm ? 'align-items-start' : 'align-items-center'
      }`}
      style={{ background: '#f4f6f8' }}
    >
      <Card className="shadow-sm" style={{ maxWidth: isForm ? 920 : 480, width: '100%' }}>
        <Card.Body className="p-4">
          <div className="text-center mb-4">
            <img
              src={process.env.PUBLIC_URL + '/logo.png'}
              alt="Re-Battery"
              style={{ width: '100%', maxWidth: 320, height: 'auto' }}
            />
            <div className="text-muted">Ενημέρωση στοιχείων σημείου συλλογής</div>
          </div>

          {loading && (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          )}

          {!loading && error && <Alert variant="danger">{apolloMessage(error)}</Alert>}

          {!loading && !error && info && (
            <>
              {!isForm && info.collectionpointName && (
                <p className="mb-2">
                  Σημείο: <strong>{info.collectionpointName}</strong>
                </p>
              )}
              {info.notificationName && <p className="text-muted mb-3">{info.notificationName}</p>}

              {info.expired && !currentAnswer && (
                <Alert variant="warning">Ο σύνδεσμος έχει λήξει.</Alert>
              )}
              {info.closed && !currentAnswer && (
                <Alert variant="warning">Η ενημέρωση έχει κλείσει.</Alert>
              )}

              {/* Εμφανίζουμε τη φόρμα για την απάντηση */}
              {isForm && info.collectionpoint && (
                <CollectionpointUpdateForm
                  canEdit={canEditForm}
                  submitting={submitting}
                  submitError={submitError}
                  saved={
                    currentAnswer === NotificationFormCompletedValue ||
                    currentAnswer === 'completed'
                  }
                  expiresAt={info.expiresAt}
                  collectionpoint={info.collectionpoint}
                  regions={info.regions ?? []}
                  subregions={info.subregions ?? []}
                  onSubmit={submitForm}
                />
              )}

              {/* Εμφανίζουμε τα κουμπιά Ναι και Όχι για την απάντηση */}
              {canAnswer && (
                <>
                  <p className="fs-5 mb-4">{questionForType(info.typeCode, info.responseType)}</p>
                  <div className="d-flex justify-content-center">
                    <Button
                      variant={currentAnswer === 'yes' ? 'success' : 'outline-success'}
                      size="lg"
                      className="me-3"
                      disabled={submitting}
                      onClick={() => submitAnswer('yes')}
                    >
                      Ναι
                    </Button>
                    <Button
                      variant={currentAnswer === 'no' ? 'danger' : 'outline-danger'}
                      size="lg"
                      disabled={submitting}
                      onClick={() => submitAnswer('no')}
                    >
                      Όχι
                    </Button>
                  </div>
                </>
              )}

              {!isForm && currentAnswer && (
                <Alert variant="success" className="mt-4 mb-0">
                  Η απάντησή σας καταχωρήθηκε: <strong>{answerLabel(currentAnswer)}</strong>
                </Alert>
              )}

              {!isForm && submitError && (
                <Alert variant="danger" className="mt-3 mb-0">
                  {submitError}
                </Alert>
              )}

              {!isForm && submitting && (
                <div className="text-center mt-3">
                  <Spinner animation="border" size="sm" />
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};
